/**
 * MODAL: CRÉATION VÉHICULE
 * Formulaire de création d'un nouveau véhicule avec validation Zod
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import type { CreateVehicleInput } from '../../schemas/vehicle';
import { createVehicleSchema } from '../../schemas/vehicle';
import { Form, FormInput, FormTextarea, FormSelect } from '../ui';
import { Modal, ModalFooter } from '../ui/modal';
import { useCrews } from '../../hooks/useCrews';

interface CreateVehicleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateVehicleInput) => Promise<void>;
}

export function CreateVehicleModal({ isOpen, onClose, onSubmit }: CreateVehicleModalProps) {
  const { data: crews } = useCrews();

  const form = useForm<CreateVehicleInput>({
    resolver: zodResolver(createVehicleSchema),
    defaultValues: {
      registration: '',
      crew_id: '',
      owner: '',
      notes: '',
    },
  });

  const handleFormSubmit = async (data: CreateVehicleInput) => {
    try {
      // Transformer les chaînes vides en undefined pour Supabase
      const cleanedData = {
        ...data,
        crew_id: data.crew_id === '' ? undefined : data.crew_id,
      };
      await onSubmit(cleanedData);
      form.reset(); // Réinitialiser le formulaire après succès
      onClose();
    } catch (error) {
      console.error('Erreur lors de la création du véhicule :', error);
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Nouveau véhicule"
      footer={
        <ModalFooter
          onCancel={handleClose}
          submitLabel="Créer le véhicule"
          isSubmitting={form.formState.isSubmitting}
          formId="create-vehicle-form"
        />
      }
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleFormSubmit)} id="create-vehicle-form">
          <div className="space-y-4">
            <FormInput
              control={form.control}
              name="registration"
              label="Immatriculation"
              placeholder="Ex: GT-264-YY"
              required
            />

            <FormSelect
              control={form.control}
              name="crew_id"
              label="Équipage affecté"
              options={[
                { value: '', label: 'Non affecté' },
                ...(crews?.map((crew) => ({
                  value: crew.id,
                  label:
                    crew.name ||
                    (crew.crew_number ? `Équipage ${crew.crew_number}` : 'Équipage sans nom'),
                })) || []),
              ]}
            />

            <FormInput
              control={form.control}
              name="circulation_date"
              label="Date de mise en circulation"
              type="date"
            />

            <FormInput
              control={form.control}
              name="owner"
              label="Propriétaire"
              placeholder="Ex: Pierre Citron"
            />

            <FormTextarea
              control={form.control}
              name="notes"
              label="Notes"
              placeholder="Ex: Alerté résevoir"
              rows={3}
            />
          </div>
        </form>
      </Form>
    </Modal>
  );
}
